import { Base64 } from 'js-base64'
import { Storage } from '@/utils/storage'
import { normalizeAccessToken } from '@/utils/auth'

export const REMEMBER_LOGIN_KEY = 'remember_login_account'
export const ACCOUNTS_STORAGE_KEY = 'accounts'
export const LOGIN_ROUTE_DRAFT_KEY = 'login_route_draft'
// 记录最后登录的账号类型，用于退出登录后切换到正确的登录 tab
export const LAST_LOGIN_ACCOUNT_TYPE_KEY = 'last_login_account_type'

const LOGIN_MEMORY_VERSION = 2
const LOGIN_MEMORY_SECRET_SALT = 'mail-task-front-end:login-memory:v2'

function getCryptoApi() {
  try {
    return globalThis?.crypto || null
  } catch (e) {
    return null
  }
}

function getTodayKey() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function createEmptyPersonalMemory() {
  return {
    accountId: '',
    account: '',
    password: '',
    phoneCode: '',
    zoneCode: '',
    updatedAt: 0,
  }
}

function createEmptyEnterpriseMemory() {
  return {
    accountId: '',
    account: '',
    companyAccount: '',
    alias: '',
    password: '',
    updatedAt: 0,
  }
}

function createEmptyRememberedState() {
  return {
    version: LOGIN_MEMORY_VERSION,
    lastSelectedLoginType: '0',
    formMemory: {
      personal: createEmptyPersonalMemory(),
      enterprise: createEmptyEnterpriseMemory(),
    },
    rememberedAccounts: [],
  }
}

function normalizeTimestamp(value) {
  const num = Number(value || 0)
  return Number.isFinite(num) && num > 0 ? num : 0
}

function normalizeAccountType(value) {
  const num = Number(value)
  return Number.isFinite(num) ? num : 1
}

export function getLoginTypeByAccountType(accountType) {
  const type = normalizeAccountType(accountType)
  return type === 2 || type === 3 ? '1' : '0'
}

export function isEnterpriseAccountType(accountType) {
  return getLoginTypeByAccountType(accountType) === '1'
}

function getMemorySecretBase() {
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'mail-task-front-end'
    return `${origin}::${LOGIN_MEMORY_SECRET_SALT}`
  } catch (e) {
    return LOGIN_MEMORY_SECRET_SALT
  }
}

async function getEncryptionKey() {
  const cryptoApi = getCryptoApi()
  if (!cryptoApi?.subtle) return null
  try {
    const secret = new TextEncoder().encode(getMemorySecretBase())
    const rawKey = await cryptoApi.subtle.digest('SHA-256', secret)
    return await cryptoApi.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
  } catch (e) {
    return null
  }
}

function encodeBytes(bytes) {
  try {
    return Base64.fromUint8Array(bytes)
  } catch (e) {
    return ''
  }
}

function decodeBytes(value) {
  try {
    return Base64.toUint8Array(String(value || ''))
  } catch (e) {
    return null
  }
}

export async function encryptLoginSecret(value) {
  const text = String(value ?? '')
  if (!text) return ''

  const cryptoApi = getCryptoApi()
  const key = await getEncryptionKey()
  if (!cryptoApi?.subtle || !key) {
    return `plain:${Base64.encode(text)}`
  }

  try {
    const iv = cryptoApi.getRandomValues(new Uint8Array(12))
    const encrypted = await cryptoApi.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(text)
    )
    return `aes:${encodeBytes(iv)}:${encodeBytes(new Uint8Array(encrypted))}`
  } catch (e) {
    return `plain:${Base64.encode(text)}`
  }
}

export async function decryptLoginSecret(value) {
  const raw = String(value || '')
  if (!raw) return ''

  if (raw.startsWith('plain:')) {
    try {
      return Base64.decode(raw.slice(6))
    } catch (e) {
      return ''
    }
  }

  if (!raw.startsWith('aes:')) {
    try {
      return Base64.decode(raw)
    } catch (e) {
      return raw
    }
  }

  const cryptoApi = getCryptoApi()
  const key = await getEncryptionKey()
  if (!cryptoApi?.subtle || !key) return ''

  try {
    const parts = raw.split(':')
    const iv = decodeBytes(parts[1])
    const payload = decodeBytes(parts[2])
    if (!iv || !payload) return ''
    const decrypted = await cryptoApi.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      payload
    )
    return new TextDecoder().decode(decrypted)
  } catch (e) {
    return ''
  }
}

function normalizePersonalMemory(raw = {}) {
  return {
    accountId: String(raw.accountId || ''),
    account: String(raw.account || '').trim(),
    password: String(raw.password || ''),
    phoneCode: String(raw.phoneCode || ''),
    zoneCode: String(raw.zoneCode || ''),
    updatedAt: normalizeTimestamp(raw.updatedAt),
  }
}

function normalizeEnterpriseMemory(raw = {}) {
  return {
    accountId: String(raw.accountId || ''),
    account: String(raw.account || '').trim(),
    companyAccount: String(raw.companyAccount || '').trim(),
    alias: String(raw.alias || '').trim(),
    password: String(raw.password || ''),
    updatedAt: normalizeTimestamp(raw.updatedAt),
  }
}

function normalizeRememberedAccount(raw = {}) {
  return {
    id: String(raw.id || ''),
    userName: String(raw.userName || ''),
    avatar: String(raw.avatar || ''),
    accountType: normalizeAccountType(raw.accountType),
    loginType: String(raw.loginType || getLoginTypeByAccountType(raw.accountType)),
    account: String(raw.account || '').trim(),
    companyAccount: String(raw.companyAccount || '').trim(),
    alias: String(raw.alias || '').trim(),
    phone: String(raw.phone || '').trim(),
    phoneCode: String(raw.phoneCode || ''),
    zoneCode: String(raw.zoneCode || ''),
    password: String(raw.password || ''),
    updatedAt: normalizeTimestamp(raw.updatedAt),
  }
}

export function readRememberedLoginState() {
  try {
    const raw = Storage.get(REMEMBER_LOGIN_KEY)
    if (!raw || typeof raw !== 'object') return createEmptyRememberedState()
    const state = createEmptyRememberedState()
    state.version = normalizeTimestamp(raw.version) || LOGIN_MEMORY_VERSION
    state.lastSelectedLoginType = String(raw.lastSelectedLoginType || '0') === '1' ? '1' : '0'
    state.formMemory.personal = normalizePersonalMemory(raw?.formMemory?.personal)
    state.formMemory.enterprise = normalizeEnterpriseMemory(raw?.formMemory?.enterprise)
    state.rememberedAccounts = (Array.isArray(raw.rememberedAccounts) ? raw.rememberedAccounts : [])
      .map((item) => normalizeRememberedAccount(item))
      .filter((item) => item.id)
    return state
  } catch (e) {
    return createEmptyRememberedState()
  }
}

export function writeRememberedLoginState(state) {
  try {
    Storage.set(REMEMBER_LOGIN_KEY, state)
  } catch (e) {
  }
}

function getLatestRememberedAccountByType(state, loginType) {
  const targetType = String(loginType || '0') === '1' ? '1' : '0'
  const list = Array.isArray(state?.rememberedAccounts) ? state.rememberedAccounts : []
  return list
    .filter((item) => String(item.loginType || '') === targetType)
    .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0))[0] || null
}

function resetFormMemoryByLoginType(state, loginType) {
  const targetType = String(loginType || '0') === '1' ? '1' : '0'
  if (targetType === '0') {
    state.formMemory.personal = createEmptyPersonalMemory()
    return
  }
  state.formMemory.enterprise = createEmptyEnterpriseMemory()
}

function syncLastSelectedLoginTypeByFormMemory(state) {
  const personalUpdatedAt = Number(state?.formMemory?.personal?.updatedAt || 0)
  const enterpriseUpdatedAt = Number(state?.formMemory?.enterprise?.updatedAt || 0)
  state.lastSelectedLoginType = enterpriseUpdatedAt > personalUpdatedAt ? '1' : '0'
}

function applyLatestFormMemoryFallback(state, loginType) {
  const targetType = String(loginType || '0') === '1' ? '1' : '0'
  const latest = getLatestRememberedAccountByType(state, targetType)
  if (targetType === '0') {
    state.formMemory.personal = latest ? {
      accountId: latest.id,
      account: latest.account,
      password: latest.password,
      phoneCode: latest.phoneCode,
      zoneCode: latest.zoneCode,
      updatedAt: latest.updatedAt,
    } : createEmptyPersonalMemory()
    return
  }

  state.formMemory.enterprise = latest ? {
    accountId: latest.id,
    account: latest.account,
    companyAccount: latest.companyAccount,
    alias: latest.alias,
    password: latest.password,
    updatedAt: latest.updatedAt,
  } : createEmptyEnterpriseMemory()
}

export async function rememberLoginAccount(payload = {}) {
  const accountId = String(payload.accountId || '')
  if (!accountId) return readRememberedLoginState()

  const state = readRememberedLoginState()
  const loginType = String(payload.loginType || getLoginTypeByAccountType(payload.accountType)) === '1' ? '1' : '0'
  const updatedAt = Date.now()
  const encryptedPassword = await encryptLoginSecret(payload.password || '')

  const nextAccount = normalizeRememberedAccount({
    id: accountId,
    userName: payload.userName,
    avatar: payload.avatar,
    accountType: payload.accountType,
    loginType,
    account: payload.account,
    companyAccount: payload.companyAccount,
    alias: payload.alias,
    phone: payload.phone,
    phoneCode: payload.phoneCode,
    zoneCode: payload.zoneCode,
    password: encryptedPassword,
    updatedAt,
  })

  const list = Array.isArray(state.rememberedAccounts) ? state.rememberedAccounts.slice() : []
  const idx = list.findIndex((item) => String(item.id || '') === accountId)
  if (idx >= 0) list.splice(idx, 1, nextAccount)
  else list.push(nextAccount)
  state.rememberedAccounts = list
  state.lastSelectedLoginType = loginType

  if (loginType === '0') {
    state.formMemory.personal = {
      accountId,
      account: nextAccount.account,
      password: encryptedPassword,
      phoneCode: nextAccount.phoneCode,
      zoneCode: nextAccount.zoneCode,
      updatedAt,
    }
  } else {
    state.formMemory.enterprise = {
      accountId,
      account: nextAccount.account,
      companyAccount: nextAccount.companyAccount,
      alias: nextAccount.alias,
      password: encryptedPassword,
      updatedAt,
    }
  }

  writeRememberedLoginState(state)
  return state
}

export function forgetRememberedAccount(payload = {}) {
  const accountId = String(payload.accountId || '')
  const loginType = String(payload.loginType || getLoginTypeByAccountType(payload.accountType)) === '1' ? '1' : '0'
  const state = readRememberedLoginState()
  const list = Array.isArray(state.rememberedAccounts) ? state.rememberedAccounts.slice() : []
  state.rememberedAccounts = list.filter((item) => {
    if (!accountId) {
      return String(item.loginType || '') !== loginType
    }
    return String(item.id || '') !== accountId
  })

  // 取消勾选“记住账号”时，该登录方式的回填信息需要重置为空；
  // 若继续回退到上一个记住过的账号，下次打开登录页仍会回填旧账号信息
  if (payload.keepFormMemoryFallback === true) {
    applyLatestFormMemoryFallback(state, loginType)
  } else {
    resetFormMemoryByLoginType(state, loginType)
  }

  syncLastSelectedLoginTypeByFormMemory(state)

  writeRememberedLoginState(state)
  return state
}

// 清空指定登录方式已记住的账号信息（含当前回填账号的记忆记录）
export function clearRememberedFormMemory(loginType) {
  const targetType = String(loginType || '0') === '1' ? '1' : '0'
  const state = readRememberedLoginState()
  const memory = targetType === '0' ? state?.formMemory?.personal : state?.formMemory?.enterprise
  const rememberedId = String(memory?.accountId || '')

  // 如果当前有记住的账号，需要先将其从 rememberedAccounts 中移除
  if (rememberedId) {
    state.rememberedAccounts = (Array.isArray(state.rememberedAccounts) ? state.rememberedAccounts : [])
      .filter((item) => String(item?.id || '') !== rememberedId)
  }

  // 重置为空的表单记忆，相当于"取消勾选记住账号"
  resetFormMemoryByLoginType(state, targetType)
  syncLastSelectedLoginTypeByFormMemory(state)

  writeRememberedLoginState(state)
  return state
}

// 记录最后登录的账号类型，用于退出登录后切换到正确的登录 tab
export function setLastLoginAccountType(accountType) {
  try {
    Storage.set(LAST_LOGIN_ACCOUNT_TYPE_KEY, String(accountType || ''))
  } catch (e) {
  }
}

// 获取最后登录的账号类型
export function getLastLoginAccountType() {
  try {
    const raw = Storage.get(LAST_LOGIN_ACCOUNT_TYPE_KEY)
    return String(raw || '')
  } catch (e) {
    return ''
  }
}

export function touchRememberedLoginAccount(payload = {}) {
  const accountId = String(payload.accountId || payload.id || '')
  const loginType = String(payload.loginType || getLoginTypeByAccountType(payload.accountType)) === '1' ? '1' : '0'
  if (!accountId) return readRememberedLoginState()

  const state = readRememberedLoginState()
  const list = Array.isArray(state.rememberedAccounts) ? state.rememberedAccounts.slice() : []
  const idx = list.findIndex((item) => String(item.id || '') === accountId)
  if (idx < 0) return state

  const updatedAt = Date.now()
  const nextAccount = {
    ...list[idx],
    updatedAt,
  }
  list.splice(idx, 1, nextAccount)
  state.rememberedAccounts = list
  state.lastSelectedLoginType = loginType

  if (loginType === '0') {
    state.formMemory.personal = {
      accountId,
      account: nextAccount.account,
      password: nextAccount.password,
      phoneCode: nextAccount.phoneCode,
      zoneCode: nextAccount.zoneCode,
      updatedAt,
    }
  } else {
    state.formMemory.enterprise = {
      accountId,
      account: nextAccount.account,
      companyAccount: nextAccount.companyAccount,
      alias: nextAccount.alias,
      password: nextAccount.password,
      updatedAt,
    }
  }

  writeRememberedLoginState(state)
  return state
}

async function hydratePersonalMemory(raw = {}) {
  const memory = normalizePersonalMemory(raw)
  return {
    ...memory,
    password: await decryptLoginSecret(memory.password),
  }
}

async function hydrateEnterpriseMemory(raw = {}) {
  const memory = normalizeEnterpriseMemory(raw)
  return {
    ...memory,
    password: await decryptLoginSecret(memory.password),
  }
}

async function hydrateRememberedAccount(raw = {}) {
  const memory = normalizeRememberedAccount(raw)
  return {
    ...memory,
    password: await decryptLoginSecret(memory.password),
  }
}

export async function getRememberedFormsSnapshot() {
  const state = readRememberedLoginState()
  const personal = await hydratePersonalMemory(state?.formMemory?.personal)
  const enterprise = await hydrateEnterpriseMemory(state?.formMemory?.enterprise)
  const personalUpdatedAt = Number(personal.updatedAt || 0)
  const enterpriseUpdatedAt = Number(enterprise.updatedAt || 0)
  const lastSelectedLoginType = enterpriseUpdatedAt > personalUpdatedAt
    ? '1'
    : (personalUpdatedAt > 0 || enterpriseUpdatedAt === 0 ? '0' : String(state.lastSelectedLoginType || '0') === '1' ? '1' : '0')

  return {
    personal,
    enterprise,
    lastSelectedLoginType,
  }
}

export async function getRememberedAccountDraftById(accountId) {
  const targetId = String(accountId || '')
  if (!targetId) return null
  const state = readRememberedLoginState()
  const hit = (Array.isArray(state.rememberedAccounts) ? state.rememberedAccounts : [])
    .find((item) => String(item.id || '') === targetId)
  if (!hit) return null
  return hydrateRememberedAccount(hit)
}

export function splitEnterpriseAccount(value) {
  const raw = String(value || '').trim()
  if (!raw) return { companyAccount: '', alias: '' }
  const atIndex = raw.indexOf('@')
  if (atIndex <= 0 || atIndex >= raw.length - 1) return { companyAccount: '', alias: '' }
  return {
    companyAccount: raw.slice(0, atIndex).trim(),
    alias: raw.slice(atIndex + 1).trim(),
  }
}

export function createLoginRouteDraft(payload = {}) {
  const loginType = String(payload.loginType || '0') === '1' ? '1' : '0'
  return {
    mode: payload.mode === 'reset' ? 'reset' : 'prefill',
    loginType,
    rememberAccount: payload.rememberAccount === true,
    personal: {
      accountId: String(payload?.personal?.accountId || ''),
      account: String(payload?.personal?.account || '').trim(),
      password: String(payload?.personal?.password || ''),
      phoneCode: String(payload?.personal?.phoneCode || ''),
      zoneCode: String(payload?.personal?.zoneCode || ''),
    },
    enterprise: {
      accountId: String(payload?.enterprise?.accountId || ''),
      account: String(payload?.enterprise?.account || '').trim(),
      companyAccount: String(payload?.enterprise?.companyAccount || '').trim(),
      alias: String(payload?.enterprise?.alias || '').trim(),
      password: String(payload?.enterprise?.password || ''),
    },
  }
}

export function writeLoginRouteDraft(payload = {}) {
  try {
    sessionStorage.setItem(LOGIN_ROUTE_DRAFT_KEY, JSON.stringify(createLoginRouteDraft(payload)))
  } catch (e) {
  }
}

export function clearLoginRouteDraft() {
  try {
    sessionStorage.removeItem(LOGIN_ROUTE_DRAFT_KEY)
  } catch (e) {
  }
}

export function readLoginRouteDraft() {
  try {
    const raw = sessionStorage.getItem(LOGIN_ROUTE_DRAFT_KEY) || ''
    if (!raw) return null
    return createLoginRouteDraft(JSON.parse(raw))
  } catch (e) {
    return null
  }
}

export function consumeLoginRouteDraft() {
  const draft = readLoginRouteDraft()
  try {
    sessionStorage.removeItem(LOGIN_ROUTE_DRAFT_KEY)
  } catch (e) {
  }
  return draft
}

function normalizeStoredAccount(raw = {}) {
  return {
    id: String(raw.id || ''),
    userName: String(raw.userName || ''),
    token: normalizeAccessToken(raw.token),
    avatar: String(raw.avatar || ''),
    account: String(raw.account || '').trim(),
    phone: String(raw.phone || '').trim(),
    accountType: normalizeAccountType(raw.accountType),
    sessionActive: raw.sessionActive !== false,
    sessionDateKey: String(raw.sessionDateKey || ''),
    lastLoginAt: normalizeTimestamp(raw.lastLoginAt),
  }
}

export function readAccountsState() {
  try {
    const raw = Storage.get(ACCOUNTS_STORAGE_KEY)
    if (!raw || typeof raw !== 'object') {
      return { accounts: [], currentToken: '', currentId: '' }
    }
    const accounts = (Array.isArray(raw.accounts) ? raw.accounts : [])
      .map((item) => normalizeStoredAccount(item))
      .filter((item) => item.id)
    return {
      accounts,
      currentToken: normalizeAccessToken(raw.currentToken),
      currentId: String(raw.currentId || ''),
    }
  } catch (e) {
    return { accounts: [], currentToken: '', currentId: '' }
  }
}

export function writeAccountsState(state) {
  try {
    Storage.set(ACCOUNTS_STORAGE_KEY, state)
  } catch (e) {
  }
}

export function isAccountSessionActive(account) {
  if (!account) return false
  const todayKey = getTodayKey()
  return Boolean(account.sessionActive !== false && String(account.token || '') && String(account.sessionDateKey || '') === todayKey)
}

export function getActiveSessionAccounts(state) {
  const next = state || readAccountsState()
  const list = Array.isArray(next.accounts) ? next.accounts : []
  return list.filter((item) => isAccountSessionActive(item))
}

export function upsertAccountSession(payload = {}) {
  const id = String(payload.id ?? payload.accountId ?? payload.userID ?? payload.guid ?? payload.account ?? '')
  const token = normalizeAccessToken(payload.token)
  if (!id) return readAccountsState()

  const state = readAccountsState()
  const list = Array.isArray(state.accounts) ? state.accounts.slice() : []
  const nextAccount = normalizeStoredAccount({
    id,
    userName: payload.userName,
    token,
    avatar: payload.avatar || payload.photoUrl,
    account: payload.account,
    phone: payload.phone,
    accountType: payload.accountType,
    sessionActive: Boolean(token),
    sessionDateKey: token ? getTodayKey() : '',
    lastLoginAt: Date.now(),
  })

  const idx = list.findIndex((item) => String(item.id || '') === id)
  if (idx >= 0) {
    list.splice(idx, 1, {
      ...list[idx],
      ...nextAccount,
      token: token || String(list[idx]?.token || ''),
      sessionActive: token ? true : list[idx]?.sessionActive !== false,
      sessionDateKey: token ? getTodayKey() : String(list[idx]?.sessionDateKey || ''),
      lastLoginAt: token ? Date.now() : Number(list[idx]?.lastLoginAt || 0),
    })
  } else {
    list.push(nextAccount)
  }

  const next = {
    accounts: list,
    currentToken: token || String(state.currentToken || ''),
    currentId: id,
  }
  if (!token) {
    next.currentToken = String(state.currentToken || '')
  }
  writeAccountsState(next)
  return next
}

export function markAccountSessionLoggedOut(accountId) {
  const targetId = String(accountId || '')
  const state = readAccountsState()
  const list = (Array.isArray(state.accounts) ? state.accounts : []).map((item) => {
    if (targetId && String(item.id || '') !== targetId) return item
    return {
      ...item,
      token: '',
      sessionActive: false,
      sessionDateKey: '',
    }
  })

  const activeAccounts = list.filter((item) => isAccountSessionActive(item))
  const next = {
    accounts: list,
    currentId: activeAccounts[0] ? String(activeAccounts[0].id || '') : '',
    currentToken: activeAccounts[0] ? String(activeAccounts[0].token || '') : '',
  }
  writeAccountsState(next)
  return next
}

export function removeAccountSession(accountId) {
  const targetId = String(accountId || '')
  if (!targetId) return readAccountsState()

  const state = readAccountsState()
  const list = (Array.isArray(state.accounts) ? state.accounts : [])
    .filter((item) => String(item?.id || '') !== targetId)

  const activeAccounts = list.filter((item) => isAccountSessionActive(item))
  const next = {
    accounts: list,
    currentId: activeAccounts[0] ? String(activeAccounts[0].id || '') : '',
    currentToken: activeAccounts[0] ? String(activeAccounts[0].token || '') : '',
  }
  writeAccountsState(next)
  return next
}

export function markAllAccountSessionsLoggedOut() {
  const next = {
    accounts: [],
    currentId: '',
    currentToken: '',
  }
  writeAccountsState(next)
  return next
}
